import { useRef, useState } from 'react';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  KNOWLEDGE_MAX_DOCUMENT_LABEL,
  KNOWLEDGE_UPLOAD_ACCEPT,
  KNOWLEDGE_UPLOAD_FORMATS_LABEL,
} from '../../config';
import { useCreateKnowledgeDocumentFromText } from '../../hooks/useKnowledge';
import { useKnowledgeDocumentUpload } from '../../hooks/useKnowledgeDocumentUpload';

/**
 * Adding a document to the account library (task 574; rebuilt in task 1084).
 *
 * A file needs no form: drop it, or browse to it, and it uploads under its own
 * name through the shared upload sequence (`useKnowledgeDocumentUpload`) — the
 * same one the Documents page drop zone and the Innovate Assistant sidebar use,
 * so a name collision gets the same Replace / Keep both / Discard prompt here as
 * everywhere else. Several files upload one after another; the dialog shows how
 * far it has got and closes when every file is settled.
 *
 * Pasted text is the secondary path, behind **Paste text instead**: there is no
 * file to name it after, so it takes a typed name and the text, nothing more.
 *
 * A created document starts `pending`; the Documents list polls it to `ready`.
 */
type View = 'files' | 'text';

interface AddKnowledgeDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddKnowledgeDocumentDialog({
  isOpen,
  onClose,
}: AddKnowledgeDocumentDialogProps) {
  const [view, setView] = useState<View>('files');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Collected per batch: the hook's `error` holds only the latest failure, and
  // a multi-file drop needs every reason.
  const batchErrorsRef = useRef<string[]>([]);
  const { upload, uploading, conflictDialog } = useKnowledgeDocumentUpload({
    surface: 'documents-page',
    onError: (message) => {
      batchErrorsRef.current.push(message);
    },
  });
  const createFromText = useCreateKnowledgeDocumentFromText();
  const isPending = uploading || progress !== null || createFromText.isPending;

  const reset = () => {
    setView('files');
    setName('');
    setContent('');
    setIsDragging(false);
    setProgress(null);
    setFileErrors([]);
    setSubmitError(null);
  };

  const handleClose = () => {
    if (isPending) return;
    reset();
    onClose();
  };

  /** One request per file, in order: the route takes one file at a time. */
  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;
    batchErrorsRef.current = [];
    setFileErrors([]);
    setProgress({ done: 0, total: files.length });
    for (const [position, file] of files.entries()) {
      await upload(file);
      setProgress({ done: position + 1, total: files.length });
    }
    setProgress(null);
    const errors = batchErrorsRef.current;
    if (errors.length === 0) {
      reset();
      onClose();
    } else {
      setFileErrors(errors);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Cleared so choosing the SAME file again still fires `change`.
    event.target.value = '';
    void uploadFiles(files);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    // Handled here, not again by the page-level drop target behind the dialog.
    event.stopPropagation();
    setIsDragging(false);
    if (isPending) return;
    void uploadFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const handleTextSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      await createFromText.mutateAsync({ name: name.trim(), content });
      reset();
      onClose();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Could not add the document. Please try again.'
      );
    }
  };

  if (!isOpen) return null;

  const canSubmitText = !isPending && name.trim().length > 0 && content.trim().length > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-knowledge-document-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3
            id="add-knowledge-document-title"
            className="text-lg font-medium text-gray-900 dark:text-white"
          >
            Add document
          </h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={isPending}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {view === 'files' ? (
          <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
            <div
              data-testid="knowledge-upload-dropzone"
              data-dragging={isDragging ? 'true' : 'false'}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center ${
                isDragging
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                Drop files here, or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isPending}
                  className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 underline disabled:opacity-50"
                >
                  browse
                </button>
              </p>
              <p
                data-testid="knowledge-upload-envelope"
                className="mt-1 text-xs text-gray-500 dark:text-gray-400"
              >
                {KNOWLEDGE_UPLOAD_FORMATS_LABEL} · up to {KNOWLEDGE_MAX_DOCUMENT_LABEL} per document
              </p>
              <input
                ref={fileInputRef}
                id="knowledge-document-file"
                aria-label="Files"
                name="file"
                type="file"
                multiple
                accept={KNOWLEDGE_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                disabled={isPending}
                className="hidden"
              />
              {progress && (
                <p
                  data-testid="knowledge-upload-progress"
                  className="mt-3 text-sm text-gray-600 dark:text-gray-300"
                >
                  Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}…
                </p>
              )}
            </div>

            {fileErrors.length > 0 && (
              <ul data-testid="knowledge-upload-error" className="space-y-1">
                {fileErrors.map((message, position) => (
                  <li key={position} className="text-sm text-red-600 dark:text-red-400">
                    {message}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  setView('text');
                  setFileErrors([]);
                }}
                disabled={isPending}
                className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 disabled:opacity-50"
              >
                Paste text instead
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {fileErrors.length > 0 ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleTextSubmit} className="flex-1 overflow-auto px-6 py-4 space-y-4">
            <div>
              <label
                htmlFor="knowledge-document-name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Name
              </label>
              <input
                id="knowledge-document-name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={isPending}
                required
                placeholder="Pricing guide"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label
                htmlFor="knowledge-document-content"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Text
              </label>
              <textarea
                id="knowledge-document-content"
                value={content}
                onChange={(event) => setContent(event.target.value)}
                disabled={isPending}
                required
                rows={8}
                placeholder="Paste the text you want the agents to be able to draw on."
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
              />
            </div>

            {submitError && (
              <p data-testid="knowledge-submit-error" className="text-sm text-red-600 dark:text-red-400">
                {submitError}
              </p>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  setView('files');
                  setSubmitError(null);
                }}
                disabled={isPending}
                className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 disabled:opacity-50"
              >
                Upload files instead
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmitText}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createFromText.isPending ? 'Adding...' : 'Add document'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
      {conflictDialog}
    </div>
  );
}

export default AddKnowledgeDocumentDialog;
